import {
  MAX_ATTEMPTS,
  WORD_LENGTH,
  type GuessEntry,
  type LetterState,
  type ModePerformance,
} from "@mk/shared";
import { recordCompletedGame } from "../../lib/adGate.ts";

/**
 * Pratik modun geçmişi.
 *
 * Pratik tamamen istemcide oynanıyor — çevrimdışı da çalışsın diye sunucuya hiç
 * uğramıyor. Geçmişi de aynı yerde tutuyoruz: tarayıcının deposunda, son
 * {@link PRACTICE_HISTORY_LIMIT} oyun. Düello geçmişinden farkı bu; o sunucudaki
 * maç kayıtlarından türüyor.
 *
 * Depodaki veri kullanıcı tarafından değiştirilebilir olduğu için okurken her
 * kayıt tek tek doğrulanır: bozuk bir satır sessizce atılır, ekran çökmez.
 */

export const PRACTICE_HISTORY_LIMIT = 10;

const STORAGE_KEY = "mk.practice.history";
const STATS_STORAGE_KEY = "mk.practice.stats.v1";
const IMPORT_STORAGE_KEY = "mk.practice.import.v1";
const INSTALLATION_KEY = "mk.practice.installation.v1";

export interface PracticeRecord {
  id: string;
  /** epoch ms */
  finishedAt: number;
  answer: string;
  solved: boolean;
  guesses: GuessEntry[];
}

export interface PracticeLifetimeStats {
  played: number;
  found: number;
  missed: number;
  totalFoundAttempts: number;
  guessDistribution: Record<string, number>;
}

const LETTER_STATES: readonly LetterState[] = ["correct", "present", "absent"];

function isGuessEntry(value: unknown): value is GuessEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as { word?: unknown; pattern?: unknown };
  if (typeof entry.word !== "string" || [...entry.word].length !== WORD_LENGTH) return false;
  return (
    Array.isArray(entry.pattern) &&
    entry.pattern.length === WORD_LENGTH &&
    entry.pattern.every((state) => LETTER_STATES.includes(state as LetterState))
  );
}

function isRecord(value: unknown): value is PracticeRecord {
  if (!value || typeof value !== "object") return false;
  const row = value as Partial<PracticeRecord>;
  return (
    typeof row.id === "string" &&
    typeof row.finishedAt === "number" &&
    Number.isFinite(row.finishedAt) &&
    typeof row.answer === "string" &&
    typeof row.solved === "boolean" &&
    Array.isArray(row.guesses) &&
    row.guesses.length <= MAX_ATTEMPTS &&
    row.guesses.every(isGuessEntry)
  );
}

/** Depodan gelen ham metni ayıkla; bozuk kayıtlar düşer. */
export function parseHistory(raw: string | null): PracticeRecord[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecord).slice(0, PRACTICE_HISTORY_LIMIT);
  } catch {
    return [];
  }
}

/** Yeni kayıt başa gelir, liste sınırda tutulur. */
export function appendRecord(
  history: readonly PracticeRecord[],
  record: PracticeRecord,
): PracticeRecord[] {
  return [record, ...history.filter((row) => row.id !== record.id)].slice(
    0,
    PRACTICE_HISTORY_LIMIT,
  );
}

function emptyLifetimeStats(): PracticeLifetimeStats {
  return { played: 0, found: 0, missed: 0, totalFoundAttempts: 0, guessDistribution: {} };
}

/** Son on kayıtla gelen eski kurulumları tüm-zamanlar sayacına başlangıç yapar. */
export function summarizePracticeRecords(
  records: readonly PracticeRecord[],
): PracticeLifetimeStats {
  return records.reduce<PracticeLifetimeStats>((stats, record) => {
    stats.played += 1;
    if (record.solved) {
      stats.found += 1;
      stats.totalFoundAttempts += record.guesses.length;
      const key = String(record.guesses.length);
      stats.guessDistribution[key] = (stats.guessDistribution[key] ?? 0) + 1;
    } else {
      stats.missed += 1;
    }
    return stats;
  }, emptyLifetimeStats());
}

export function appendPracticeStats(
  current: PracticeLifetimeStats,
  record: PracticeRecord,
): PracticeLifetimeStats {
  const next: PracticeLifetimeStats = {
    ...current,
    guessDistribution: { ...current.guessDistribution },
    played: current.played + 1,
  };
  if (record.solved) {
    next.found += 1;
    next.totalFoundAttempts += record.guesses.length;
    const key = String(record.guesses.length);
    next.guessDistribution[key] = (next.guessDistribution[key] ?? 0) + 1;
  } else {
    next.missed += 1;
  }
  return next;
}

export function isPracticeLifetimeStats(value: unknown): value is PracticeLifetimeStats {
  if (!value || typeof value !== "object") return false;
  const stats = value as Partial<PracticeLifetimeStats>;
  return (
    Number.isInteger(stats.played) && stats.played! >= 0 &&
    Number.isInteger(stats.found) && stats.found! >= 0 &&
    Number.isInteger(stats.missed) && stats.missed! >= 0 &&
    stats.played === stats.found! + stats.missed! &&
    Number.isInteger(stats.totalFoundAttempts) && stats.totalFoundAttempts! >= 0 &&
    Boolean(stats.guessDistribution) &&
    typeof stats.guessDistribution === "object" &&
    Object.entries(stats.guessDistribution!).every(
      ([key, count]) =>
        /^[1-6]$/.test(key) && Number.isInteger(count) && count >= 0,
    )
  );
}

function loadPracticeLifetimeStats(): PracticeLifetimeStats {
  try {
    const raw = window.localStorage.getItem(STATS_STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isPracticeLifetimeStats(parsed)) return parsed;
    }
  } catch {
    /* Son 10 kayıttan güvenli başlangıç üretilecek. */
  }
  return summarizePracticeRecords(loadPracticeHistory());
}

function savePracticeLifetimeStats(stats: PracticeLifetimeStats): void {
  try {
    window.localStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(stats));
  } catch {
    /* Kota/depo hatası oyunu etkilememeli. */
  }
}

export function loadPracticePerformance(): ModePerformance {
  const local = loadPracticeLifetimeStats();
  const imported = loadImportedPracticeStats();
  const stats: PracticeLifetimeStats = {
    played: local.played + imported.played,
    found: local.found + imported.found,
    missed: local.missed + imported.missed,
    totalFoundAttempts: local.totalFoundAttempts + imported.totalFoundAttempts,
    guessDistribution: { ...local.guessDistribution },
  };
  for (const [attempt, count] of Object.entries(imported.guessDistribution)) {
    stats.guessDistribution[attempt] = (stats.guessDistribution[attempt] ?? 0) + count;
  }
  return {
    played: stats.played,
    found: stats.found,
    missed: stats.missed,
    averageAttempts:
      stats.found > 0 ? stats.totalFoundAttempts / stats.found : null,
    guessDistribution: { ...stats.guessDistribution },
  };
}

export interface PracticeArchive {
  format: "mk-practice-v1";
  sourceId: string;
  history: PracticeRecord[];
  stats: PracticeLifetimeStats;
}

function loadImportedPracticeStats(): PracticeLifetimeStats {
  try {
    const raw = window.localStorage.getItem(IMPORT_STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isPracticeLifetimeStats(parsed)) return parsed;
    }
  } catch { /* No imported archive on this device. */ }
  return emptyLifetimeStats();
}

export function exportPracticeArchive(): PracticeArchive {
  let sourceId = window.localStorage.getItem(INSTALLATION_KEY);
  if (!sourceId) {
    sourceId = crypto.randomUUID();
    window.localStorage.setItem(INSTALLATION_KEY, sourceId);
  }
  return {
    format: "mk-practice-v1",
    sourceId,
    history: loadPracticeHistory(),
    stats: loadPracticeLifetimeStats(),
  };
}

export function parsePracticeArchive(raw: string): PracticeArchive {
  if (raw.length > 1_000_000) throw new Error("archive_too_large");
  const value: unknown = JSON.parse(raw);
  if (!value || typeof value !== "object") throw new Error("invalid_archive");
  const archive = value as Partial<PracticeArchive>;
  if (archive.format !== "mk-practice-v1" ||
      typeof archive.sourceId !== "string" || !/^[0-9a-f-]{36}$/i.test(archive.sourceId) ||
      !Array.isArray(archive.history) ||
      archive.history.length > PRACTICE_HISTORY_LIMIT ||
      !archive.history.every(isRecord) ||
      !isPracticeLifetimeStats(archive.stats)) throw new Error("invalid_archive");
  return archive as PracticeArchive;
}

/** One migration snapshot per device. Replacing it cannot double count totals. */
export function importPracticeArchive(raw: string): void {
  const archive = parsePracticeArchive(raw);
  if (archive.sourceId === window.localStorage.getItem(INSTALLATION_KEY)) return;
  const existing = loadPracticeHistory();
  const ids = new Set(existing.map((row) => row.id));
  const merged = [...existing, ...archive.history.filter((row) => !ids.has(row.id))]
    .sort((a, b) => b.finishedAt - a.finishedAt)
    .slice(0, PRACTICE_HISTORY_LIMIT);
  // Persist local lifetime stats first, before merged history could become its fallback.
  savePracticeLifetimeStats(loadPracticeLifetimeStats());
  window.localStorage.setItem(IMPORT_STORAGE_KEY, JSON.stringify(archive.stats));
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
}

function makeId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `p${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function loadPracticeHistory(): PracticeRecord[] {
  try {
    return parseHistory(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Gizli sekmede depo kapalı olabilir; geçmişsiz devam.
    return [];
  }
}

/** Biten bir pratik oyunu kaydeder ve güncel listeyi döner. */
export function savePracticeRun(input: {
  answer: string;
  solved: boolean;
  guesses: readonly GuessEntry[];
}): PracticeRecord[] {
  const record: PracticeRecord = {
    id: makeId(),
    finishedAt: Date.now(),
    answer: input.answer,
    solved: input.solved,
    guesses: [...input.guesses],
  };
  const next = appendRecord(loadPracticeHistory(), record);
  savePracticeLifetimeStats(appendPracticeStats(loadPracticeLifetimeStats(), record));
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Kota dolmuşsa oyun aksamasın.
  }
  recordCompletedGame(`practice:${record.id}`);
  return next;
}

export function clearPracticeHistory(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(STATS_STORAGE_KEY);
    window.localStorage.removeItem(IMPORT_STORAGE_KEY);
  } catch {
    /* yok sayılır */
  }
}
